import React, { useState, useEffect, useRef } from 'react';

interface Message {
  id: number;
  text: string;
  isAI: boolean;
  timestamp: Date;
}

interface AIChatProps {
  onClose: () => void;
}

const AIChat: React.FC<AIChatProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const aiResponses = [
    "👋 Hey there! I'm your AI study buddy. I'm super excited about the new mock test features we're building!",
    "🧠 These aren't just regular tests - they'll adapt to YOUR learning style and focus on areas where you need the most help.",
    "📊 Want to know what's coming? AI-powered question selection, real-time performance analysis, and personalized study recommendations!",
    "🎯 I'll be able to identify your weak topics within seconds and suggest the perfect practice questions.",
    "⚡ The best part? Instant detailed explanations for every answer, generated specifically for your learning level!",
    "🚀 While you wait, why don't you explore your current progress in the Report Card section?",
    "💡 Or check out the dashboard to see how other students are performing. Competition makes learning fun!",
    "🎉 I can't wait for you to experience the future of personalized learning. It's going to be AMAZING!",
    "📧 Make sure to sign up for notifications so you're the first to know when we launch!",
    "🤔 Is there anything specific about mock tests you'd like to know? I'm here to help!"
  ];

  const userPrompts = [
    "Tell me more about the AI features",
    "How will the adaptive testing work?",
    "What makes these tests special?",
    "When will this be available?",
    "Show me a preview"
  ];

  useEffect(() => {
    // Start the conversation automatically
    const timer = setTimeout(() => {
      addAIMessage(0);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const addAIMessage = (index: number) => {
    if (index >= aiResponses.length) return;

    setIsTyping(true);
    
    // Simulate typing delay
    setTimeout(() => {
      const newMessage: Message = {
        id: Date.now(),
        text: aiResponses[index],
        isAI: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, newMessage]);
      setIsTyping(false);
      
      // Auto-continue conversation with delays
      if (index < aiResponses.length - 1) {
        setTimeout(() => {
          addAIMessage(index + 1);
        }, 3000 + Math.random() * 2000); // Random delay between 3-5 seconds
      }
    }, 1500 + Math.random() * 1000); // Random typing time
  };

  const handleUserPrompt = (prompt: string) => {
    const userMessage: Message = {
      id: Date.now(),
      text: prompt,
      isAI: false,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    // AI responds based on the prompt
    const responses = {
      "Tell me more about the AI features": "🤖 Our AI analyzes your performance patterns and creates personalized question sets. It learns from every answer you give!",
      "How will the adaptive testing work?": "📈 The system adjusts difficulty in real-time. Too easy? Harder questions coming up! Struggling? I'll give you questions to build confidence first.",
      "What makes these tests special?": "✨ Unlike static tests, ours evolve with you! Plus, you'll get AI-generated explanations tailored to your specific learning style.",
      "When will this be available?": "🗓️ We're in final testing phases! Sign up for notifications - you'll be among the first to experience this revolution in learning!",
      "Show me a preview": "🎬 I wish I could show you the interface! Imagine charts that update live, questions that feel like they're made just for you, and insights that help you grow every day."
    };
    
    setTimeout(() => {
      const aiResponse: Message = {
        id: Date.now() + 1,
        text: responses[prompt as keyof typeof responses] || "That's a great question! 🤔 The development team is working on some amazing features. Stay tuned!",
        isAI: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
    }, 1000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  return (
    <div className="position-fixed" style={{ 
      bottom: '20px', 
      right: '20px', 
      width: '350px', 
      maxHeight: '500px',
      zIndex: 1000
    }}>
      <div className="card border-0 shadow-lg" style={{ borderRadius: '20px' }}>
        {/* Chat Header */}
        <div className="card-header border-0 p-3" style={{ 
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '20px 20px 0 0'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div className="position-relative">
                <div className="rounded-circle bg-white d-flex align-items-center justify-content-center me-2" 
                     style={{ width: '35px', height: '35px' }}>
                  <i className="fas fa-robot text-primary"></i>
                </div>
                <div className="position-absolute top-0 end-0 bg-success rounded-circle" 
                     style={{ width: '10px', height: '10px' }}></div>
              </div>
              <div>
                <h6 className="text-white mb-0 fw-bold">AI Study Assistant</h6>
                <small className="text-white-50">Always here to help!</small>
              </div>
            </div>
            <button 
              className="btn btn-sm text-white" 
              onClick={onClose}
              style={{ padding: '4px 8px' }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="card-body p-3" style={{ 
          height: '300px', 
          overflowY: 'auto',
          background: '#f8f9ff'
        }}>
          {messages.map((message) => (
            <div key={message.id} className={`d-flex mb-3 ${message.isAI ? 'justify-content-start' : 'justify-content-end'}`}>
              <div className={`p-2 rounded-3 ${message.isAI ? 'bg-white text-dark' : 'bg-primary text-white'}`}
                   style={{ maxWidth: '80%', fontSize: '14px' }}>
                {message.text}
                <div className={`small mt-1 ${message.isAI ? 'text-muted' : 'text-white-50'}`}>
                  {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="d-flex justify-content-start mb-3">
              <div className="bg-white p-2 rounded-3" style={{ maxWidth: '80%' }}>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="card-footer border-0 p-3" style={{ borderRadius: '0 0 20px 20px' }}>
          <div className="d-flex flex-wrap gap-1">
            {userPrompts.map((prompt, index) => (
              <button
                key={index}
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: '12px', borderRadius: '15px' }}
                onClick={() => handleUserPrompt(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .typing-indicator {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .typing-indicator span {
            height: 8px;
            width: 8px;
            background-color: #667eea;
            border-radius: 50%;
            animation: typing 1.4s infinite ease-in-out;
          }
          .typing-indicator span:nth-child(1) {
            animation-delay: -0.32s;
          }
          .typing-indicator span:nth-child(2) {
            animation-delay: -0.16s;
          }
          @keyframes typing {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `
      }} />
    </div>
  );
};

export default AIChat;